import { Mongo } from 'meteor/mongo';
import { DeclareMethod } from '/imports/methods';
import { Doc, ListMethodArg, ListMethodResponse, GetMethodResponse, ModelClass, References } from '/imports/types';
import { validateString } from '/imports/validators';

export interface SellTarget extends Doc {
  _id: string;
  cardId: number;
  card: string;
  /** Optional per-card stocksize override. When set, the bot tries to keep
   *  this many copies in stock instead of `bot.options.stocksize`. */
  sellcap?: number;
  /** Optional minimum sell price; bot won't sell below this. */
  minprice?: number;
}

type Model = SellTarget;
type Refs = References<Model>;

export const SellTargets = new Mongo.Collection<SellTarget>('sellTargets');

export const listSellTargets = DeclareMethod<ListMethodArg<SellTarget>, ListMethodResponse<SellTarget, Refs>>('listSellTargets');
export const getSellTarget = DeclareMethod<string, GetMethodResponse<SellTarget, Refs>>('getSellTarget');
export const createSellTarget = DeclareMethod<Partial<SellTarget>, string>('createSellTarget');
export const updateSellTarget = DeclareMethod<Partial<SellTarget>, string>('updateSellTarget');
export const deleteSellTarget = DeclareMethod<string, undefined>('deleteSellTarget');

/** Bulk-create sell-targets selected by one of {id, name(+set,+foil), set,
 *  all} with optional rarity/foil/price filters. Existing sell-targets for
 *  the same cardId are replaced. */
export interface BulkAddSellTargetsConfig {
  // exactly one of these selectors should be set
  id?: number;
  name?: string;
  set?: string;      // comma-separated for the "all cards in set(s)" selector
  all?: boolean;
  // filters (applied in name/set/all modes; NOT in id mode)
  rarity?: string;
  isfoil?: 'True' | 'False';
  minprice?: number;
  maxprice?: number;
  pricekey?: string;       // default 'manSellPrice'
  withprices?: boolean;
  // foil filter for `name` selector only
  foil?: boolean;
  // attributes for the new sell-target docs
  sellcap?: number;
  sellcapMin?: number;     // optional minprice to set on the row
}
export interface BulkAddSellTargetsResult {
  added: number;
  skipped: number;
  errors: string[];
}
export const bulkAddSellTargets = DeclareMethod<BulkAddSellTargetsConfig, BulkAddSellTargetsResult>('bulkAddSellTargets');

export const validateSellTarget = async (target: Partial<SellTarget>) => {
  const errors: { [key: string]: string[] } = {};
  validateString(errors, target, 'card', { required: true });
  return errors;
};

export const modelClass: ModelClass<Model, Refs> = {
  collection: SellTargets,
  editableFields: ['cardId', 'card', 'sellcap', 'minprice'],
  listMethod: listSellTargets,
  getMethod: getSellTarget,
  createMethod: createSellTarget,
  updateMethod: updateSellTarget,
  deleteMethod: deleteSellTarget,
  pathPrefix: '/sell-targets',
  modelName: 'SellTarget',
  pluralName: 'Sell Targets',
  validate: validateSellTarget,
};
